import { cn } from '@/lib/utils';
import { Loader2 } from 'lucide-react';

interface LoadingSpinnerProps {
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Use the Lucide Loader2 icon style instead of CSS border spinner */
  variant?: 'border' | 'icon';
  className?: string;
}

const sizeClasses = {
  sm: 'h-4 w-4',
  md: 'h-6 w-6',
  lg: 'h-8 w-8',
};

/**
 * Consistent loading spinner used across the app.
 *
 * ```tsx
 * <LoadingSpinner />                            // default border spinner
 * <LoadingSpinner size="sm" variant="icon" />   // small Loader2 icon
 * ```
 */
export function LoadingSpinner({
  size = 'lg',
  variant = 'border',
  className,
}: LoadingSpinnerProps) {
  if (variant === 'icon') {
    return <Loader2 className={cn('text-primary animate-spin', sizeClasses[size], className)} />;
  }

  return (
    <div
      className={cn(
        'border-primary animate-spin rounded-full border-4 border-t-transparent',
        sizeClasses[size],
        className
      )}
    />
  );
}

/**
 * Full-width centered loading state — drop-in replacement for page/section loaders.
 */
export function LoadingPage({ className }: { className?: string }) {
  return (
    <div className={cn('flex items-center justify-center py-12', className)}>
      <LoadingSpinner />
    </div>
  );
}
